import { Box, IconButton, Theme, Tooltip, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Column } from 'react-data-grid';
import {
  Exclusion,
  ExclusionsMatchTypes,
  ExclusionSortKey,
  ExclusionType,
} from '../types/exclusion-types';
import DateFormat from '../../elements/Date/DateFormat';
import ActionIcon from '../components/ActionIcon';
import ApplicationCell from '../../elements/TableCells/ApplicationCell';
import ExclusionsOptionsContainer from '../containers/ExclusionsOptionsContainer';
import OwnerCell from '../../elements/TableCells/OwnerCell';
import { FaRegCommentDots } from 'react-icons/fa';
import { MdAlarmOff, MdAlarmOn } from 'react-icons/md';

export const useExclusionsTableHeader = (): Column<Exclusion>[] => {
  const { classes, cx } = useStyles();

  const columns: Column<Exclusion>[] = [
    // {
    //   key: 'index',
    //   name: '#',
    //   width: 40,
    //   minWidth: 40,
    //   maxWidth: 40,
    //   cellClass: cx(classes.textCenter, classes.textSecondary),
    //   headerCellClass: classes.textCenter,
    //   headerRenderer: () => {
    //     return <>#</>;
    //   },
    //   formatter: ({ row }) => {
    //     return <>{row.index + 1}</>;
    //   },
    // },
    {
      key: ExclusionSortKey.ExclusionType,
      name: 'Type',
      width: 50,
      minWidth: 50,
      maxWidth: 50,
      cellClass: classes.textCenter,
      formatter: ({ row }) => {
        return (
          <Tooltip
            title={
              <Typography variant='body2' color='text.primary'>
                {ExclusionType[row.exclusionType]}
              </Typography>
            }>
            <div>
              <ActionIcon type={row.exclusionType} />
            </div>
          </Tooltip>
        );
      },
    },
    {
      key: ExclusionSortKey.AppliedOn,
      name: 'Issue',
      formatter: ({ row }) => {
        return (
          <Tooltip arrow placement='top' title={row.exclusionAppliedOn}>
            <Typography
              variant='body2'
              color='text.primary'
              className={classes.textOverflow}>
              {row.exclusionAppliedOn}
            </Typography>
          </Tooltip>
        );
      },
    },

    {
      key: ExclusionSortKey.AppName,
      name: 'App Name',
      width: 170,
      minWidth: 170,
      maxWidth: 170,
      cellClass: classes.textSecondary,
      formatter: ({ row }) => {
        return row.exclusionMatch &&
          row.exclusionMatch[0] &&
          row.exclusionMatch[0].key === 'policy' ? (
          <Typography variant='body2' color='text.secondary'>
            N/A
          </Typography>
        ) : (
          <ApplicationCell appName={row.appName} appType={row.appType} />
        );
      },
    },
    {
      key: ExclusionSortKey.Match,
      name: 'Match',
      width: 200,
      minWidth: 200,
      maxWidth: 200,
      formatter: ({ row }) => {
        return (
          <Tooltip
            title={row.exclusionMatch.map((match, index) => {
              return (
                <Typography variant='body2' color='text.primary' key={index}>
                  {match.value}
                </Typography>
              );
            })}>
            <Typography
              className={classes.match}
              variant='body2'
              color='text.primary'
              component='span'
              noWrap>
              {row.exclusionMatch.map((match, index) => {
                return (
                  <p key={index} className={classes.matchDescription}>
                    {ExclusionsMatchTypes[match.key]} : {match.value}
                  </p>
                );
              })}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      key: ExclusionSortKey.ModifiedIssues,
      name: 'Count	',
      width: 60,
      minWidth: 60,
      maxWidth: 60,
      cellClass: cx(classes.textCenter, classes.textSecondary),
      formatter: ({ row }) => {
        return (
          <Typography variant='body2' color='text.primary'>
            {row.modifiedIssues}
          </Typography>
        );
      },
    },
    {
      key: ExclusionSortKey.ModifiedBy,
      name: 'Created By',
      width: 150,
      minWidth: 150,
      maxWidth: 150,
      cellClass: classes.textSecondary,
      formatter: ({ row }) => {
        return <OwnerCell owners={[row.modifiedBy]} />;
      },
    },
    {
      key: ExclusionSortKey.Comment,
      name: 'Comment',
      width: 90,
      minWidth: 90,
      maxWidth: 90,
      cellClass: cx(classes.textCenter, classes.textSecondary),
      formatter: ({ row }) => {
        return row.comment ? (
          <Tooltip
            arrow
            placement='top'
            title={
              <Typography variant='body2' color='text.primary'>
                {row.comment}
              </Typography>
            }>
            <IconButton
              color='primary'
              size='small'
              style={{ backgroundColor: 'transparent' }}>
              <FaRegCommentDots size='18px' />
            </IconButton>
          </Tooltip>
        ) : null;
      },
    },
    {
      key: ExclusionSortKey.Snooze,
      name: 'Snooze Till',
      width: 100,
      minWidth: 100,
      maxWidth: 100,
      cellClass: cx(classes.textCenter, classes.textSecondary),
      formatter: ({ row }) => {
        return row.expiredAt ? (
          <Tooltip
            arrow
            placement='top'
            title={
              <Typography variant='body2' color='text.primary'>
                {row.isActive
                  ? `Exclusion will expire on: ${
                      row.expiredAt instanceof Date
                        ? row.expiredAt.toLocaleDateString()
                        : new Date(row.expiredAt).toLocaleDateString('en-US')
                    }`
                  : `Exclusion no longer in effect. It expired on:  ${
                      row.expiredAt instanceof Date
                        ? row.expiredAt.toLocaleDateString()
                        : new Date(row.expiredAt).toLocaleDateString('en-US')
                    }`}
              </Typography>
            }>
            <IconButton
              color='primary'
              size='small'
              style={{ backgroundColor: 'transparent' }}>
              {row.isActive ? (
                <MdAlarmOn size='20px' />
              ) : (
                <MdAlarmOff size='20px' />
              )}
            </IconButton>
          </Tooltip>
        ) : null;
      },
    },
    {
      key: ExclusionSortKey.ExcludedDate,
      name: 'Applied Date',
      width: 100,
      minWidth: 100,
      maxWidth: 100,
      cellClass: classes.textSecondary,
      formatter: ({ row }) => {
        return <DateFormat date={new Date(row.createdAt)} />;
      },
    },
    {
      key: ExclusionSortKey.MenuOptions,
      name: '',
      width: 60,
      minWidth: 60,
      maxWidth: 60,
      cellClass: classes.textSecondary,
      formatter: ({ row }) => {
        return (
          <Box display='flex' justifyContent='center'>
            <ExclusionsOptionsContainer exclusion={row} />
          </Box>
        );
      },
    },
  ];

  return columns;
};

const useStyles = makeStyles()((theme: Theme) => ({
  textCenter: {
    justifyContent: 'center',
  },
  textSecondary: {
    color: theme.palette.text.secondary,
  },
  match: {
    display: 'flex',
    flexDirection: 'column',
  },
  matchDescription: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    display: 'inline-block',
    whiteSpace: 'nowrap',
    flexGrow: 1,
  },
  textOverflow: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordBreak: 'break-word',
    display: '-webkit-box',
    whiteSpace: 'pre-wrap',
    lineClamp: 2,
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  },
}));
